import type {
  AnnuaireDuServeur,
  CallNote,
  Comment,
  Conversation,
  CreateEventInput,
  CurrentUser,
  DestinationProgress,
  FeedCursor,
  FeedPage,
  FriendRequest,
  FriendSuggestion,
  FriendshipStatus,
  HelpTopic,
  LangueParlee,
  LoginInput,
  Message,
  NotificationSettings,
  Post,
  PostMention,
  PrivacySettings,
  PublicUser,
  RegisterInput,
  ReportReason,
  SiroccoEvent,
  VoiceMessage,
} from '@sirocco/shared';

import * as firebaseAuth from '../firebase/auth';
import * as tableauData from '../firebase/tableau';
import * as annonceData from '../firebase/annonce';
import * as sondeData from '../firebase/sonde';
import * as carteData from '../firebase/carte';
import * as blocksData from '../firebase/blocks';
import * as callsData from '../firebase/calls';
import type { CallHandlers, IncomingCall } from '../firebase/calls';
import type { VideoAEnvoyer } from '../utils/video';
import { requireServerId } from '../firebase/context';
import * as eventsData from '../firebase/events';
import * as friendsData from '../firebase/friends';
import * as messagesData from '../firebase/messages';
import type { ChoseAPartager } from '../firebase/messages';
import * as moderationData from '../firebase/moderation';
import * as associationsData from '../firebase/associations';
import * as likesDesLieuxData from '../firebase/lieux';
import * as parcoursData from '../firebase/parcours';
import * as postsData from '../firebase/posts';
import * as profilesData from '../firebase/profiles';
import * as pushData from '../firebase/push';
import * as supportData from '../firebase/support';
import * as positionData from '../firebase/position';
import {
  FENETRE_DU_FIL,
  SORTIES_MINIMUM,
  choisirDesSorties,
  classerLeFil,
  filAssezDense,
  sortiesProposables,
  type PostAClasser,
  type SortieAClasser,
} from '../pourtoi/classement';
import * as pourToi from '../pourtoi/journal';
import type { Geste } from '../pourtoi/journal';
import type { NiveauDeLecture } from '../pourtoi/lecture';
import type { ModeDuFil } from '../pourtoi/preference';
import { debutDe } from '../pourtoi/traits';
import { etatDeLaMiseAJour } from '../native/maj';
import { langue, t } from '../i18n';

/**
 * Toute la surface de donnees de l'application, en un seul endroit.
 *
 * Ce fichier a d'abord appele une API Express, il parle desormais
 * directement a Firebase. Les signatures ont ete conservees a
 * l'identique, et c'est volontaire : les vingt-sept ecrans consomment
 * ces fonctions sans savoir ce qu'il y a derriere.
 *
 * Seule exception : like, signet, suppression, signalement et
 * participation recoivent l'objet complet plutot que son identifiant.
 * Sans serveur pour retrouver la fiche, la lui redemander couterait un
 * aller-retour alors que l'ecran l'a deja sous la main.
 *
 * Le spot courant n'est plus deduit par un serveur a chaque requete : il
 * est retenu par `firebase/context` et injecte ici, ce qui evite de le
 * faire remonter dans la signature de chaque appel.
 */

/* --- Authentification --------------------------------------------- */

export const auth = {
  /** Renvoie le courriel de confirmation d'adresse. */
  resendVerification: () => firebaseAuth.renvoyerLaVerification(),
  /** Redemande a Firebase si l'adresse est confirmee, maintenant. */
  checkVerified: () => firebaseAuth.verifierLAdresse(),
  /** Ce qu'on sait deja de la confirmation, sans aller voir. */
  isVerified: () => firebaseAuth.adresseConfirmee(),
  login: (input: LoginInput) => firebaseAuth.login(input),
  register: (input: RegisterInput) => firebaseAuth.register(input),
  changePassword: (current: string, next: string) =>
    firebaseAuth.changePassword(current, next).then(() => ({ ok: true })),
  forgotPassword: (email: string) =>
    firebaseAuth
      .forgotPassword(email)
      .then(() => ({ ok: true, message: t.moderation.merci.emailParti })),
};

/* --- Spots --------------------------------------------------------- */

export const servers = {
  list: (search = '') => profilesData.listServers(search),
  get: (serverId: string) => profilesData.getServer(serverId),
  join: (serverId: string) => profilesData.joinServer(serverId),
  /** Les serveurs prives, parmi ceux-ci, dont j'ai la cle. */
  accesPrives: (serverIds: string[]) => profilesData.mesAccesPrives(serverIds),


  /* Frapper a la porte d'une ville qui n'a pas ouvert. */
};

/* --- Pour toi ------------------------------------------------------ */

/**
 * Le fil de l'accueil, tel que l'ecran l'affiche.
 *
 * `dense` dit si la ville publie assez pour qu'un classement ait un sens :
 * c'est lui qui fait paraitre le choix « Pour toi / Récent ». `classe` dit
 * si l'ordre rendu est celui du classement.
 */
export interface FilDAccueil extends FeedPage {
  dense: boolean;
  classe: boolean;
}

/** Les sorties a venir, et celles qui me ressemblent quand il y en a assez. */
export interface SortiesAVenir {
  sorties: SiroccoEvent[];
  pourToi: SiroccoEvent[];
}

const versPostVu = (post: Post) => ({ id: post.id, auteur: post.author.id, texte: post.content });

const versPostAClasser = (post: Post): PostAClasser => ({
  ...versPostVu(post),
  creeLe: Date.parse(post.createdAt),
  likes: post.likeCount,
  commentaires: post.commentCount,
  signets: post.bookmarkCount,
});

const versSortieVue = (event: SiroccoEvent) => ({
  id: event.id,
  categorie: event.category,
  organisateur: event.organizer.id,
  date: event.date,
  heure: event.startTime,
});

const versSortieAClasser = (event: SiroccoEvent): SortieAClasser => ({
  ...versSortieVue(event),
  debut: debutDe(event.date, event.startTime),
  participants: event.participants.map((membre) => membre.id),
  places: event.maxParticipants,
  noteOrganisateur: event.organizerRating?.moyenne ?? null,
  inscrit: event.myStatus !== 'none',
  annulee: event.statut === 'annule',
});

/**
 * Fait evoluer le journal des gouts de ce telephone.
 *
 * Jamais bloquant, jamais visible : un geste qui ne s'inscrit pas — pas de
 * session, stockage refuse — ne doit rien casser de
 * l'action qui l'a produit. Rend le journal ecrit, ou `null`.
 */
function retenir(
  modifier: (journal: pourToi.Journal, maintenant: number, uid: string) => pourToi.Journal,
): pourToi.Journal | null {
  try {
    const uid = firebaseAuth.requireUid();
    const maintenant = Date.now();
    const journal = modifier(pourToi.lireLeJournal(uid, maintenant), maintenant, uid);
    pourToi.ecrireLeJournal(uid, journal);
    return journal;
  } catch {
    return null;
  }
}

function noterLeGeste(geste: Geste): void {
  retenir((journal, maintenant) => pourToi.noter(journal, geste, maintenant));
}

/** Mes proches dans la ville : mes amis, et ceux que j'ai croises a une sortie. */
async function mesProches(serverId: string): Promise<{ amis: Set<string>; rencontres: Set<string> }> {
  const [amis, rencontres] = await Promise.all([
    friendsData.listFriendIds().catch((): string[] => []),
    friendsData.listMetIds(serverId).catch((): string[] => []),
  ]);
  return { amis: new Set(amis), rencontres: new Set(rencontres) };
}

/**
 * Le journal, avec mes sorties relues au plus une fois par jour.
 *
 * Ce que j'ai fait avant que le journal existe, ou depuis un autre
 * telephone, n'y figure pas : « Mes evenements » le sait, et le rattrapage
 * se fait ici, le jour ou les suggestions en ont besoin.
 */
async function journalDesSorties(uid: string, maintenant: number): Promise<pourToi.Journal> {
  const journal = pourToi.lireLeJournal(uid, maintenant);
  if (maintenant - journal.amorce < 24 * 3_600_000) return journal;
  try {
    const mes = await eventsData.listMine();
    const amorce = pourToi.amorcerDesSorties(
      journal,
      {
        venues: mes.past.map(versSortieVue),
        inscrites: mes.joined.map(versSortieVue),
        organisees: mes.organized.map(versSortieVue),
      },
      maintenant,
    );
    pourToi.ecrireLeJournal(uid, amorce);
    return amorce;
  } catch {
    return journal;
  }
}

/* --- Feed ---------------------------------------------------------- */

export const posts = {
  /** Une page du fil ; sans curseur, la plus recente. */
  feed: (before?: FeedCursor, search?: string, serverId?: string) =>
    postsData.listFeed(serverId ?? requireServerId(), before, search),
  /**
   * Le fil de l'accueil : classe pour moi quand la ville publie assez et que
   * je n'ai pas choisi « Récent », chronologique sinon.
   *
   * Le classement porte sur les cent posts les plus recents ; la suite
   * arrive ensuite dans l'ordre des dates, comme avant. Une ville calme ne
   * coute rien de plus : on s'arrete a la premiere page.
   */
  fil: async (mode: ModeDuFil): Promise<FilDAccueil> => {
    const serverId = requireServerId();
    const premiere = await postsData.listFeed(serverId);
    const maintenant = Date.now();
    const dense = filAssezDense(
      premiere.posts.map((post) => Date.parse(post.createdAt)),
      premiere.cursor !== null,
      maintenant,
    );
    if (!dense || mode === 'recent') {
      return { ...premiere, dense, classe: false };
    }

    const [suite, proches] = await Promise.all([
      postsData.listFeed(
        serverId,
        premiere.cursor ?? undefined,
        undefined,
        Math.max(1, FENETRE_DU_FIL - premiere.posts.length),
      ),
      mesProches(serverId),
    ]);
    const fenetre = [...premiere.posts, ...suite.posts];
    const moi = firebaseAuth.requireUid();
    const journal =
      retenir((lu, instant, uid) =>
        pourToi.apprendreDuFil(
          lu,
          fenetre.map((post) => ({ ...versPostVu(post), aime: post.liked, enregistre: post.bookmarked })),
          uid,
          instant,
        ),
      ) ?? pourToi.journalVide(maintenant);

    return {
      posts: classerLeFil(fenetre, versPostAClasser, { maintenant, moi, ...proches, journal }),
      cursor: suite.cursor,
      dense: true,
      classe: true,
    };
  },
  /** Un post reste a l'ecran : vu au bout d'une seconde, lu au bout de quatre. */
  lecture: (post: Post, niveau: NiveauDeLecture) =>
    noterLeGeste(
      niveau === 'vu' ? { quoi: 'vu', postId: post.id } : { quoi: 'lecture', post: versPostVu(post) },
    ),
  bookmarks: () => postsData.listBookmarked(),
  byAuthor: (userId: string) => postsData.listByAuthor(userId),
  /* Qui a aime l'un de mes posts. L'ecran ne le propose qu'a son
     auteur : un like ne se montre nulle part ailleurs. */
  quiAAime: (postId: string) => postsData.quiAAime(postId),
  /** Un post seul, quand on arrive sans l'avoir sous la main. */
  byId: (postId: string) => postsData.readPost(postId),
  create: (
    content: string,
    imageUrl: string | null = null,
    location: string | null = null,
    attachedEventId: string | null = null,
    mentions: PostMention[] = [],
    /** Le fil d'une association dont on est membre. */
    asso: string | null = null,
  ) =>
    postsData.createPost(requireServerId(), content, imageUrl, location, attachedEventId, mentions, asso),
  /** Suppression definitive, refusee si le post n'est pas le tien. */
  remove: (post: Pick<Post, 'id' | 'serverId' | 'author'>) =>
    postsData.deletePost(post).then(() => ({ ok: true })),
  like: (post: Post) =>
    postsData.toggleLike(post).then((apres) => {
      noterLeGeste({ quoi: 'like', post: versPostVu(apres), annule: !apres.liked });
      return apres;
    }),
  bookmark: (post: Post) =>
    postsData.toggleBookmark(post).then((apres) => {
      noterLeGeste({ quoi: 'signet', post: versPostVu(apres), annule: !apres.bookmarked });
      return apres;
    }),
  /** Avec le post sous la main, le fil apprend aussi a ne plus en montrer de pareils. */
  hide: (postId: string, post?: Post) =>
    postsData.hidePost(postId).then(() => {
      if (post) noterLeGeste({ quoi: 'masque', post: versPostVu(post) });
      return { ok: true };
    }),
  /** Le post revient dans le fil. */
  unhide: (postId: string) => postsData.unhidePost(postId).then(() => ({ ok: true })),
  /** Les posts masques, pour pouvoir revenir dessus. */
  hidden: (): Promise<Post[]> => postsData.listHiddenPosts(),
  /** Signaler masque aussi le post : voir `posts.reportPost`. */
  report: (post: Pick<Post, 'id' | 'serverId'>, reason: ReportReason, details = '') =>
    postsData.reportPost(post, reason, details).then(() => ({
      ok: true,
      message: t.moderation.merci.post,
    })),
  comments: (postId: string) => postsData.listComments(postId),
  /** `parentId` repond a une reponse ; sans lui, la reponse va sous le post. */
  comment: (
    postId: string,
    content: string,
    parentId: string | null = null,
    mentions: PostMention[] = [],
  ) =>
    postsData.addComment(postId, content, parentId, mentions).then((commentaire) => {
      // Le post n'est pas sous la main : il comptera a sa prochaine lecture dans le fil.
      retenir((journal) => pourToi.mettreEnAttente(journal, `p:${postId}`, 'c'));
      return commentaire;
    }),
  /** Like d'un commentaire, et signet : la meme bascule que sur un post. */
  likeComment: (comment: Comment) => postsData.toggleCommentLike(comment),
  bookmarkComment: (comment: Comment) => postsData.toggleCommentBookmark(comment),
  bookmarkedComments: () => postsData.listBookmarkedComments(),
  removeComment: (comment: Comment) => postsData.deleteComment(comment).then(() => ({ ok: true })),
  reportComment: (comment: Pick<Comment, 'id' | 'postId'>, reason: ReportReason, details = '') =>
    postsData.reportComment(comment, reason, details).then(() => ({
      message: t.moderation.merci.signalement,
    })),
};

/* --- Evenements ---------------------------------------------------- */

/**
 * L'agenda d'un membre, tel que la couche donnee le compose.
 * Le comptage pour les trophees en fait partie : les regles qui ecartent
 * un evenement vecu seul ou un second evenement dans la journee vivent
 * la-bas, pas dans l'ecran.
 */
export type MyEventsResponse = eventsData.MyEvents;

export const events = {
  /** Ou en est l'autorisation de position. */
  positionState: () => positionData.etatPosition(),
  /** Eteint le compteur de kilometres, s'il tournait encore. */
  eteindreLeCompteurDeKm: async () => {
    if (await positionData.suiviActif()) await positionData.arreterSuivi();
  },
  list: (search = '') => eventsData.listEvents(requireServerId(), search),
  /**
   * Les sorties a venir, et trois au plus qui me ressemblent.
   *
   * Rien de plus n'est lu tant que la liste est courte, qu'on cherche, ou
   * que les suggestions sont coupees : la liste arrive alors exactement
   * comme avant.
   */
  aVenir: async (search = ''): Promise<SortiesAVenir> => {
    const serverId = requireServerId();
    const sorties = await eventsData.listEvents(serverId, search);
    if (search) return { sorties, pourToi: [] };

    const maintenant = Date.now();
    const moi = firebaseAuth.requireUid();
    if (sortiesProposables(sorties, versSortieAClasser, moi, maintenant).length < SORTIES_MINIMUM) {
      return { sorties, pourToi: [] };
    }
    const [proches, journal] = await Promise.all([
      mesProches(serverId),
      journalDesSorties(moi, maintenant),
    ]);
    return {
      sorties,
      pourToi: choisirDesSorties(sorties, versSortieAClasser, { maintenant, moi, ...proches, journal }),
    };
  },
  get: (eventId: string) => eventsData.getEvent(eventId, requireServerId()),
  mine: (): Promise<MyEventsResponse> => eventsData.listMine(),
  bookmarks: () => eventsData.listBookmarked(),
  /**
   * Ou je suis, pour le filtre de distance.
   *
   * `null` quand on ne peut pas savoir — autorisation refusee, releve
   * impossible. Le point ne part nulle part : il sert a comparer dans la
   * page, et l'ecran le redemande a chaque fois qu'il en a besoin.
   */
  whereAmI: () => positionData.monPoint(),
  /** Le jeton de courte duree qui ouvre la carte Apple des sorties. */
  jetonDeCarte: () => carteData.jetonDeCarte(),
  create: (input: CreateEventInput) => eventsData.createEvent(requireServerId(), input),
  join: (event: SiroccoEvent) =>
    eventsData.joinEvent(event).then((apres) => {
      noterLeGeste({
        quoi: 'inscription',
        sortie: versSortieVue(apres),
        annule: apres.myStatus === 'none',
      });
      return apres;
    }),
  leave: (event: SiroccoEvent) =>
    eventsData.leaveEvent(event).then((apres) => {
      noterLeGeste({ quoi: 'inscription', sortie: versSortieVue(apres), annule: true });
      return apres;
    }),
  /** Le groupe d'une sortie : y suis-je, et comment y revenir. */
  dansLeGroupe: (groupId: string | null) => eventsData.suisJeDansLeGroupe(groupId),
  rejoindreLeGroupe: (event: SiroccoEvent) => eventsData.rejoindreLeGroupe(event),
  bookmark: (event: SiroccoEvent) =>
    eventsData.toggleBookmark(event).then((apres) => {
      noterLeGeste({ quoi: 'signet-sortie', sortie: versSortieVue(apres), annule: !apres.bookmarked });
      return apres;
    }),
  /** Reserves a l'organisateur : accepter, refuser, retirer, transmettre. */
  approve: (event: SiroccoEvent, participantId: string) =>
    eventsData.approveParticipant(event, participantId),
  remove: (event: SiroccoEvent, participantId: string) =>
    eventsData.removeParticipant(event, participantId),
  handOver: (event: SiroccoEvent, successorId: string) =>
    eventsData.handOverEvent(event, successorId),
  /** Le titre, le jour, les horaires. Pas les places : voir `updateEvent`. */
  update: (event: SiroccoEvent, edit: eventsData.EventEdit) => eventsData.updateEvent(event, edit),
  /** La presence sur place : qui est venu, et comment on l'a constate. */
  attendance: (eventId: string) => eventsData.readAttendance(eventId),

  /* La feuille de presence : ce geste transforme une absence de marque en
     absence tout court, et il ne se fait qu'une fois. */
  sheetClosedAt: (eventId: string) => eventsData.readSheetClosedAt(eventId),
  closeSheet: (event: SiroccoEvent) => eventsData.closeAttendanceSheet(event),

  /* Ou j'en suis vis-a-vis des absences, et la note de l'organisateur. */
  sanction: () => eventsData.readSanction(),
  rate: (event: SiroccoEvent, note: number) =>
    eventsData.rateOrganizer(event, note).then(() => {
      noterLeGeste({ quoi: 'note', sortie: versSortieVue(event), note });
      return { ok: true };
    }),
  myRating: (event: SiroccoEvent) => eventsData.readMyRating(event),
  organizerRating: (organizerId: string) => eventsData.readOrganizerRating(organizerId),
  markPresent: (event: SiroccoEvent, participantId: string, via: 'qr' | 'manuel') =>
    eventsData.markAttendance(event, participantId, via),
  clearPresent: (event: SiroccoEvent, participantId: string) =>
    eventsData.clearAttendance(event, participantId),
  /** Retient la conversation ouverte pour l'evenement, une bonne fois. */
  linkGroup: (event: SiroccoEvent, conversationId: string) =>
    eventsData.linkGroup(event, conversationId).then(() => ({ ok: true })),
  destroy: (event: SiroccoEvent) => eventsData.deleteEvent(event).then(() => ({ ok: true })),

  /** Moderation : masquer chez moi, signaler pour l'equipe. */
  /** Avec la sortie sous la main, les suggestions apprennent a ne plus en proposer de pareilles. */
  hide: (eventId: string, event?: SiroccoEvent) =>
    eventsData.hideEvent(eventId).then(() => {
      if (event) noterLeGeste({ quoi: 'masque-sortie', sortie: versSortieVue(event) });
      return { ok: true };
    }),
  unhide: (eventId: string) => eventsData.unhideEvent(eventId).then(() => ({ ok: true })),
  /** Ce que j'ai masque, pour pouvoir revenir dessus. */
  hidden: () => eventsData.listHidden(),
  /** Les lieux deja donnes aux evenements du spot : sur un campus, ils se reproposent. */
  lieuxDejaDonnes: () => eventsData.lieuxDejaDonnes(requireServerId()),
  report: (event: Pick<SiroccoEvent, 'id' | 'serverId'>, reason: ReportReason, details = '') =>
    eventsData.reportEvent(event, reason, details).then(() => ({
      ok: true,
      message: t.moderation.merci.sortie,
    })),
};

/* --- Messages ------------------------------------------------------ */

export const messages = {
  /** Toutes destinations confondues : la messagerie est inter-serveurs. */
  conversations: () => messagesData.listConversations(),
  /** Juste le nombre : la pastille de la barre d'onglets s'en contente. */
  conversation: (id: string) => messagesData.getConversation(id),
  contacts: () => messagesData.listContacts(),
  thread: (id: string) => messagesData.listThread(id),
  /** Le texte de mes fils, pour y chercher un mot sans les marquer lus. */
  textes: (ids: string[]) => messagesData.lireLesTextes(ids),
  /** Qui a lu jusqu'ou : la feuille « Infos » d'un message. */
  readCursors: (id: string) => messagesData.lireReperes(id),
  /** Les emojis du fil, lus avec lui pour qu'il s'affiche fini. */
  reactions: (id: string) => messagesData.lireReactions(id),
  /** L'emoji pose sur un message ; `null` retire le sien. */
  reagir: (id: string, messageId: string, emoji: string | null) =>
    messagesData.reagir(id, messageId, emoji),
  watchReactions: (id: string, onChange: (r: messagesData.Reactions) => void) =>
    messagesData.watchReactions(id, onChange),
  send: (
    id: string,
    content: string,
    attachedEventId: string | null = null,
    imageUrl: string | null = null,
    voice: VoiceMessage | null = null,
    video: VideoAEnvoyer | null = null,
    replyTo: string | null = null,
  ) =>
    messagesData.sendMessage(
      id, content, attachedEventId, imageUrl, voice, video, null, replyTo,
    ),
  removeMessage: (id: string, messageId: string) =>
    messagesData.deleteMessage(id, messageId).then(() => ({ ok: true })),
  modifierMessage: (id: string, messageId: string, content: string) =>
    messagesData.editMessage(id, messageId, content),
  mute: (id: string) => messagesData.toggleMute(id),
  /** Envoie un post dans la discussion directe avec quelqu'un. */
  partagerA: (otherId: string, quoi: ChoseAPartager) =>
    messagesData.partagerAvec(otherId, requireServerId(), quoi),
  partagerDans: (conversationId: string, quoi: ChoseAPartager) =>
    messagesData.partagerDans(conversationId, quoi),
  /** Depose la trace d'un appel dans la discussion directe. */
  noteCall: (otherId: string, note: CallNote) =>
    messagesData.noteCall(otherId, requireServerId(), note),
  report: (id: string, reason: ReportReason, details = '') =>
    messagesData.reportConversation(id, reason, details).then(() => ({
      ok: true,
      message: t.moderation.merci.simple,
    })),
  /** Les demandes de message, et combien attendent une reponse. */
  /** Ecoutes en direct : un message recu s'affiche sans rien recharger. */
  watchThread: messagesData.watchThread,
  /* « Il écrit… » : une marque horodatee, bornee au fil ouvert. */
  signalerFrappe: messagesData.signalerFrappe,
  arreterFrappe: messagesData.arreterFrappe,
  watchFrappe: messagesData.watchFrappe,
  watchFrappeDesFils: messagesData.watchFrappeDesFils,
  /* Les messages qui m'attendent, en direct et pour trois fois rien. */
  watchNonLus: messagesData.watchNonLus,
  watchNonLusParFil: messagesData.watchNonLusParFil,
  /* De quoi annoncer un message sans le marquer comme lu. */
  apercu: messagesData.apercuDuFil,
  watchConversations: messagesData.watchConversations,
  requests: () => messagesData.listRequests(),
  requestCount: () => messagesData.countRequests(),
  markRequestsSeen: () => messagesData.markRequestsSeen().then(() => ({ ok: true })),
  openDirect: (userId: string) => messagesData.openDirect(userId, requireServerId()),
  createGroup: (
    title: string,
    description: string,
    memberIds: string[],
    avatarUrl: string | null = null,
    eventId: string | null = null,
  ) =>
    messagesData.createGroup(
      requireServerId(),
      title,
      description,
      memberIds,
      avatarUrl,
      eventId,
    ),
  addMembers: (id: string, memberIds: string[]) => messagesData.addMembers(id, memberIds),
  setGroupPhoto: (id: string, avatarUrl: string | null) =>
    messagesData.setGroupPhoto(id, avatarUrl),
  /** Renommer un groupe : reserve a son administrateur. */
  renameGroup: (id: string, title: string) => messagesData.renameGroup(id, title),
  /** Sa description, meme reserve. */
  describeGroup: (id: string, description: string) =>
    messagesData.describeGroup(id, description),
  /** Passer la main : un groupe n'a qu'un administrateur a la fois. */
  setGroupAdmin: (id: string, memberId: string) => messagesData.setGroupAdmin(id, memberId),
  removeGroupMember: (id: string, memberId: string) =>
    messagesData.removeGroupMember(id, memberId).then(() => ({ ok: true })),
  /** L'admin qui part designe son repreneur : voir `messages.leaveGroup`. */
  leaveGroup: (id: string, successorId?: string) =>
    messagesData.leaveGroup(id, successorId).then(() => ({ ok: true })),
  /** Retire la conversation de ma liste ; quitte le groupe si c'en est un. */
  removeConversation: (id: string, successorId?: string) =>
    messagesData.deleteConversation(id, successorId).then(() => ({ ok: true })),
};

/* --- Membres ------------------------------------------------------- */

export interface MyProfileResponse {
  user: CurrentUser;
  destinations: DestinationProgress[];
  posts: Post[];
  events: SiroccoEvent[];
  stats: { totalPoints: number; level: number; cities: number };
}

export interface PublicProfileResponse {
  user: PublicUser;
  friendshipStatus: FriendshipStatus;
  /** true si je l'ai bloque : sa fiche reste ouverte, mais vide. */
  blocked: boolean;
  /** true si je l'ai masque : ses posts et ses sorties ne paraissent plus chez moi. */
  hidden: boolean;
  /** true si le profil est reserve aux amis et que je n'en fais pas partie. */
  restricted: boolean;
  destinations: DestinationProgress[];
  posts: Post[];
  events: SiroccoEvent[];
  eventsHidden: boolean;
}

export interface SettingsResponse {
  username: string;
  email: string;
  currentServerId: string | null;
  privacy: PrivacySettings;
  notifications: NotificationSettings;
  blockedCount: number;
  appVersion: string;
}

export interface FriendsResponse {
  requests: FriendRequest[];
  suggestions: FriendSuggestion[];
  results: FriendSuggestion[];
}

/** Releves temporaires d'un appareil : voir `firebase/sonde.ts`. */
export const sonde = {
  demandee: (nom: string): Promise<boolean> => sondeData.sondeDemandee(nom).catch(() => false),
  noter: (nom: string, lignes: object[]): Promise<void> => sondeData.noterReleves(nom, lignes),
};

export const users = {
  /**
   * Ce que ce telephone fait tourner, ecrit au demarrage.
   *
   * Sur le web il n'y a pas de coquille : rien a ecrire. Un zero, lui, se
   * note — il dit une coquille anterieure a la 1.7, celle qui a appris a
   * se nommer, et c'est exactement ce qu'on cherche a reperer.
   */
  /**
   * L'annonce d'une version, et si je dois la voir quoi qu'il arrive.
   *
   * La demande posee sous un compte sert a la relire une fois, meme deja
   * a jour : c'est ainsi qu'on verifie ce que les autres recoivent.
   */
  annonce: (plateforme: 'ios' | 'android') => annonceData.lireLAnnonce(plateforme),
  annonceVue: () => annonceData.oublierLaDemande(),
  noterMaVersion: async (): Promise<void> => {
    const etat = await etatDeLaMiseAJour();
    if (!etat) return;
    await profilesData.noterLaVersion({
      coquille: etat.coquille,
      version: etat.version,
      interface: etat.interface,
    });
  },
  /** La langue de l'application, pour les messages qui partent du serveur. */
  retenirMaLangue: (): Promise<void> => profilesData.retenirLaLangue(langue),
  myProfile: (): Promise<MyProfileResponse> => profilesData.myProfile(),
  /** Le statut « en ligne » d'un membre, tenu a jour en direct. */
  watchPresence: (userId: string, onChange: (online: boolean) => void) =>
    profilesData.watchPresence(userId, onChange),
  /** Recherche par pseudo, sans notion d'amitie : pour peupler un groupe. */
  search: (query: string): Promise<PublicUser[]> => profilesData.searchMembers(query),
  publicProfile: (userId: string): Promise<PublicProfileResponse> =>
    profilesData.publicProfile(userId),
  myEvents: (): Promise<MyEventsResponse> => eventsData.listMine(),
  settings: (): Promise<SettingsResponse> => profilesData.settings(),
  update: (patch: {
    bio?: string;
    username?: string;
    avatarUrl?: string | null;
    languesParlees?: LangueParlee[];
  }) => profilesData.updateProfile(patch),
  updatePrivacy: (patch: Partial<PrivacySettings>) => profilesData.updatePrivacy(patch),
  updateNotifications: (patch: Partial<NotificationSettings>) =>
    profilesData.updateNotifications(patch),
  /** Efface le compte et tout ce qu'il a seme. Deconnecte en repartant. */
  deleteAccount: (): Promise<void> => profilesData.deleteAccount(),
  setNightMode: (nightMode: boolean) => profilesData.setNightMode(nightMode),
  /**
   * Demandes recues et suggestions.
   * Une recherche remplace les suggestions par ses resultats, comme
   * avant : c'est l'ecran "Ajouter des amis" qui decide quoi afficher.
   */
  /** Mes amis, par ordre alphabetique. Distinct de `friends`, qui sert a en nouer. */
  friendList: (): Promise<PublicUser[]> => friendsData.listFriends(),
  /** Tous les membres du serveur ou l'on est, pour son onglet Annuaire. */
  annuaire: (): Promise<AnnuaireDuServeur> => friendsData.listerLAnnuaire(requireServerId()),
  /** Mon CV dans l'ecole ou je suis, et celui d'un autre membre. */
  /** Ma formation, qui me range dans son bloc de l'annuaire ; `null` pour ne plus la dire. */
  enregistrerMaFormation: (formation: string | null): Promise<void> =>
    parcoursData.enregistrerMaFormation(requireServerId(), formation),
  /**
   * `tour` avance d'une fenetre de suggestions : c'est ce que fait le
   * geste de tirage sur l'ecran d'ajout. Voir `friends.listSuggestions`.
   */
  friends: async (search = '', tour = 0): Promise<FriendsResponse> => {
    const [requests, suggestions, results] = await Promise.all([
      friendsData.listRequests(),
      search ? Promise.resolve([]) : friendsData.listSuggestions(requireServerId(), tour),
      search ? friendsData.searchUsers(search) : Promise.resolve([]),
    ]);
    return { requests, suggestions, results };
  },
  /** Tous les comptes de l'application, pour l'administrateur. */
  tousLesMembres: (): Promise<FriendSuggestion[]> => friendsData.listerTousLesMembres(),
  requestFriend: (userId: string) =>
    friendsData.requestFriend(userId, requireServerId()).then((status) => ({ status })),
  /** Demandes d'ami non encore vues, et le repere qui les eteint. */
  newRequestCount: () => friendsData.countNewRequests(),
  watchRequests: friendsData.watchRequests,
  markRequestsSeen: () => friendsData.markRequestsSeen().then(() => ({ ok: true })),
  acceptRequest: (requestId: string) =>
    friendsData.acceptRequest(requestId, requireServerId()).then(() => ({ ok: true })),
  declineRequest: (requestId: string) =>
    friendsData.declineRequest(requestId).then(() => ({ ok: true })),
  removeFriend: (userId: string) => friendsData.removeFriend(userId).then(() => ({ ok: true })),

  /* --- Moderation d'un membre -------------------------------------- */
  block: (userId: string) => blocksData.blockUser(userId).then(() => ({ ok: true })),
  unblock: (userId: string) => blocksData.unblockUser(userId).then(() => ({ ok: true })),
  blocked: (): Promise<PublicUser[]> => blocksData.listBlockedProfiles(),
  /** Masquer un compte : ne plus le voir, sans rien rompre. Voir `blocks.listHidden`. */
  hide: (userId: string) => blocksData.hideUser(userId).then(() => ({ ok: true })),
  unhide: (userId: string) => blocksData.unhideUser(userId).then(() => ({ ok: true })),
  hiddenAccounts: (): Promise<PublicUser[]> => blocksData.listHiddenProfiles(),
  /** Combien chaque liste de Masques et bloques montrera. */
  ecartes: async () => {
    const [comptesMasques, comptesBloques, posts, evenements] = await Promise.all([
      blocksData.listHidden().then((ids) => ids.length),
      blocksData.listBlocked().then((ids) => ids.length),
      postsData.countHiddenPosts(),
      eventsData.countHidden(),
    ]);
    return { comptesMasques, comptesBloques, posts, evenements };
  },
  /** Signaler bloque aussi : voir `blocks.reportUser`. */
  report: (userId: string, reason: ReportReason, details = '') =>
    blocksData.reportUser(userId, reason, details).then(() => ({
      ok: true,
      message: t.moderation.merci.membre,
    })),
};

/* --- Aide et signalements ------------------------------------------ */

/**
 * La moderation. Reservee aux porteurs d'un role, verifie cote serveur.
 */
/*
  Ce que voit celui qui tient l'application : des comptes, des
  horodatages, des nombres. Le calcul se fait au serveur, qui seul a le
  droit de lire la fiche de tout le monde.
*/
export const admin = {
  tableauDeBord: () => tableauData.lireLeTableau(),
};

export const moderation = {
  myRole: () => moderationData.readMyRole(),
  list: () => moderationData.listReports(),
  act: (
    action: moderationData.ActionModeration,
    motif: string,
    reportId?: string,
    cible?: string,
    details?: moderationData.DetailsDuGeste,
  ) => moderationData.agir(action, motif, reportId, cible, details).then(() => ({ ok: true })),
  /** Tout ce qui pese sur un membre, pour l'ecran d'administration. */
  dossier: (uid: string) => moderationData.lireLeDossier(uid),
  /** L'adresse d'un membre : pour le proprietaire de l'application seul. */
  adresse: (uid: string) => moderationData.lireLAdresse(uid),
};

export const support = {
  send: (topic: HelpTopic, message: string) =>
    supportData.sendHelpRequest(topic, message).then(() => ({
      ok: true,
      message: t.moderation.merci.support,
    })),
};

/* --- Notifications sur le telephone --------------------------------- */

/**
 * L'appareil a joindre quand l'application est fermee.
 *
 * Rien ne s'en sert encore : la signature actuelle de l'application n'a
 * pas le droit d'enregistrer un appareil aupres d'Apple, donc aucun
 * jeton n'existe. Tout le reste de la chaine attend derriere.
 */
export const likesDesLieux = {
  /** Se tient a jour tant que la fiche est ouverte. Rend de quoi s'arreter. */
  ecouter: likesDesLieuxData.ecouterLesLikes,
  poser: likesDesLieuxData.poserLeLike,
};

export const push = {
  register: (token: string) => pushData.saveDeviceToken(token),
  forget: (token: string) => pushData.removeDeviceToken(token),
  /**
   * La fiche de l'application dans les reglages du telephone, la ou l'on
   * rallume les notifications. La meme porte que pour la position, et
   * presente sur toutes les coquilles depuis la 1.5 (11). `false` si elle
   * ne s'est pas ouverte.
   */
  ouvrirLesReglagesDuTelephone: () => positionData.ouvrirLesReglages(),
};

/* --- Associations d'une ecole ---------------------------------------- */

/**
 * Les associations du serveur courant : leur liste, leur page, leur fil,
 * et comment y entrer. Tout le monde les lit ; seuls leurs membres y
 * publient, et seul leur chef accepte une demande.
 */
export const associations = {
  list: () => associationsData.listerLesAssociations(requireServerId()),
  page: (asso: string) => associationsData.lireLAssociation(requireServerId(), asso),
  fil: (asso: string) => postsData.listerLesPostsDeLAsso(requireServerId(), asso),
  demander: (asso: string) => associationsData.demanderARejoindre(requireServerId(), asso),
  annulerDemande: (asso: string) => associationsData.annulerMaDemande(requireServerId(), asso),
  accepter: (asso: string, uid: string) => associationsData.accepter(requireServerId(), asso, uid),
  refuser: (asso: string, uid: string) => associationsData.refuser(requireServerId(), asso, uid),
  retirer: (asso: string, uid: string) => associationsData.retirer(requireServerId(), asso, uid),
  quitter: (asso: string) => associationsData.quitter(requireServerId(), asso),
  /** Le groupe de l'association : son identifiant, apres m'y avoir fait revenir s'il le faut. */
  groupe: (asso: string) => associationsData.ouvrirLeGroupe(requireServerId(), asso),
};

/* --- Appels ---------------------------------------------------------- */

/**
 * Appeler un membre. La voix ne passe pas par la base : elle ne sert qu'a
 * presenter les deux telephones l'un a l'autre.
 */
export const calls = {
  place: (calleeId: string, handlers: CallHandlers, avecVideo = false) =>
    callsData.placeCall(calleeId, handlers, avecVideo),
  answer: (callId: string, handlers: CallHandlers) => callsData.answerCall(callId, handlers),
  decline: (callId: string) => callsData.declineCall(callId),
  watch: (onCall: (liste: IncomingCall[]) => void) => callsData.watchIncomingCalls(onCall),
};

/** Re-export : certains ecrans typent leurs listes avec ces formes. */
export type { Comment, Conversation, Message };
export type { MessageEcrit } from '../firebase/messages';
export type { TrophySkip, Attendance, AttendanceMark, EventEdit } from '../firebase/events';
export { EDITABLE_UNTIL_MS, editableFor } from '../firebase/events';
